<script setup>
import { computed, ref } from 'vue'
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { Badge } from '@/components/ui/badge/index.js'
import { Button } from '@/components/ui/button/index.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { Input } from '@/components/ui/input/index.js'
import { useChecklistTemplatesStore } from '../../stores/checklistTemplatesStore.js'

const props = defineProps({
  checklistTemplateable: {
    type: Object,
    default: null,
  },
  attachedChecklistTemplateIds: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['attached'])
const { componentRef: dialog, openModal, closeModal } = useModalControl()
const checklistTemplatesStore = useChecklistTemplatesStore('select-modal')

const searchQuery = ref('')
const selectedChecklistTemplateId = ref(null)
const isLoading = ref(false)
const errorMessage = ref(null)

const normalizedChecklistTemplateableId = computed(() => Number(props.checklistTemplateable?.id ?? 0))
const normalizedChecklistTemplateableType = computed(() => {
  return String(props.checklistTemplateable?.type ?? '').trim()
})
const hasChecklistTemplateableContext = computed(() => {
  return normalizedChecklistTemplateableId.value > 0 && normalizedChecklistTemplateableType.value.length > 0
})
const attachedChecklistTemplateIdSet = computed(() => {
  return new Set(
    props.attachedChecklistTemplateIds
      .map((checklistTemplateId) => Number(checklistTemplateId))
      .filter((checklistTemplateId) => checklistTemplateId > 0),
  )
})
const availableChecklistTemplates = computed(() => {
  return checklistTemplatesStore.items.filter((checklistTemplate) => {
    const checklistTemplateId = Number(checklistTemplate.id ?? 0)
    if (checklistTemplateId < 1 || attachedChecklistTemplateIdSet.value.has(checklistTemplateId)) {
      return false
    }

    if (checklistTemplate.status === 'archived') {
      return false
    }

    const isDraftVersion = checklistTemplate.status === 'draft'
      && Number(checklistTemplate.draft_of_id ?? 0) > 0

    return !isDraftVersion
  })
})
const canSubmit = computed(() => {
  return !isLoading.value && Number(selectedChecklistTemplateId.value ?? 0) > 0 && hasChecklistTemplateableContext.value
})
const emptyStateMessage = computed(() => {
  if (!hasChecklistTemplateableContext.value) {
    return 'A valid checklist template attachment target is required.'
  }

  if (isLoading.value) {
    return 'Loading checklist templates...'
  }

  if (searchQuery.value.trim().length > 0) {
    return 'No checklist templates match your search.'
  }

  return 'No attachable checklist templates are available.'
})

const formatChecklistTemplateStatus = (status) => {
  const normalizedStatus = String(status ?? '').trim()
  if (normalizedStatus.length === 0) {
    return 'Unknown'
  }

  return normalizedStatus
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
}

const resetState = () => {
  searchQuery.value = ''
  selectedChecklistTemplateId.value = null
  errorMessage.value = null
  checklistTemplatesStore.items = []
}

const loadChecklistTemplates = async () => {
  if (!hasChecklistTemplateableContext.value) {
    checklistTemplatesStore.items = []
    errorMessage.value = 'A valid checklist template attachment target is required.'
    return
  }

  isLoading.value = true
  errorMessage.value = null

  try {
    await checklistTemplatesStore.fetchItems({
      clear: true,
      not_paginated: true,
      owner_only: true,
      status: 'published',
      order_by: 'title',
      order_direction: 'asc',
      ...(searchQuery.value.trim().length > 0 ? { search: searchQuery.value.trim() } : {}),
    })
  } catch (error) {
    errorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to load checklist templates.'
  } finally {
    isLoading.value = false
  }
}

const handleShow = async () => {
  resetState()
  await loadChecklistTemplates()
}

const handleSearch = async () => {
  selectedChecklistTemplateId.value = null
  await loadChecklistTemplates()
}

const handleOk = async (bvEvent) => {
  if (bvEvent) {
    bvEvent.preventDefault()
  }

  errorMessage.value = null

  if (!canSubmit.value) {
    errorMessage.value = 'Select a checklist template to attach.'
    return
  }

  isLoading.value = true

  try {
    const attachedChecklistTemplate = await checklistTemplatesStore.attachChecklistTemplate(selectedChecklistTemplateId.value, {
      checklist_templateable_id: normalizedChecklistTemplateableId.value,
      checklist_templateable_type: normalizedChecklistTemplateableType.value,
    })

    emit('attached', attachedChecklistTemplate)
    closeModal()
  } catch (error) {
    errorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to attach checklist template.'
  } finally {
    isLoading.value = false
  }
}

defineExpose({
  openModal,
  closeModal,
})
</script>

<template>
  <UmisisModal
    ref="dialog"
    title="Attach Existing Checklist Template"
    description="Search checklist templates by title or description, then attach one to this record."
    ok-title="Attach selected template"
    cancel-title="Cancel"
    size="lg"
    :ok-disabled="!canSubmit"
    @ok="handleOk"
    @show="handleShow"
  >
    <div class="flex flex-col gap-4">
      <Field>
        <FieldLabel for="checklist-template-select-search">
          Search
        </FieldLabel>
        <div class="flex gap-2">
          <Input id="checklist-template-select-search" v-model="searchQuery" placeholder="Filter by title or description" :disabled="isLoading" @keydown.enter.prevent="handleSearch" />
          <Button type="button" variant="outline" :disabled="isLoading" @click="handleSearch">
            Search
          </Button>
        </div>
      </Field>

      <FieldDescription v-if="errorMessage" class="font-medium text-destructive">
        {{ errorMessage }}
      </FieldDescription>

      <div v-if="availableChecklistTemplates.length > 0" class="max-h-96 overflow-y-auto rounded-md border">
        <label
          v-for="checklistTemplate in availableChecklistTemplates"
          :key="checklistTemplate.id"
          :for="`checklist-template-select-${checklistTemplate.id}`"
          class="flex cursor-pointer items-start gap-3 border-b p-3 transition-colors hover:bg-muted/50 last:border-b-0"
        >
          <input
            :id="`checklist-template-select-${checklistTemplate.id}`"
            v-model="selectedChecklistTemplateId"
            type="radio"
            name="checklist-template-select"
            class="mt-1"
            :value="Number(checklistTemplate.id)"
          >
          <div class="flex min-w-0 flex-1 flex-col gap-1">
            <div class="flex flex-wrap items-center gap-2">
              <span class="font-medium text-foreground">
                {{ checklistTemplate.title || `Checklist template #${checklistTemplate.id}` }}
              </span>
              <Badge variant="outline">
                {{ formatChecklistTemplateStatus(checklistTemplate.status) }}
              </Badge>
            </div>
            <p v-if="checklistTemplate.description" class="text-sm text-muted-foreground">
              {{ checklistTemplate.description }}
            </p>
            <p class="text-xs text-muted-foreground">
              Template ID #{{ checklistTemplate.id }}
            </p>
          </div>
        </label>
      </div>

      <p v-else class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
        {{ emptyStateMessage }}
      </p>
    </div>
  </UmisisModal>
</template>
